/**
 * This file defines a context for managing login state in a React application.
 * Context provides a way to pass data through the component tree without having to pass props down manually at every level.
 * For more information, refer to the official React documentation:
 * https://react.dev/learn/passing-data-deeply-with-context
 */

import { createContext, ReactNode, useContext, useEffect, useState } from "react";

type AuthState = "loading" | "authenticated" | "unauthenticated";

type AuthContextType = {
    isLoggedIn: boolean;
    authState: AuthState;
    authLoading: boolean;
    login: () => void;
    logout: () => void;
    setAuthState: (state: AuthState) => void;
};

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children, loggedIn }: { children: ReactNode; loggedIn?: boolean }) => {
    const [isLoggedIn, setIsLoggedIn] = useState(!!loggedIn);
    const [authState, setAuthState] = useState<AuthState>("loading");

    // Update auth state when loggedIn prop changes
    useEffect(() => {
        if (loggedIn !== undefined) {
            setIsLoggedIn(!!loggedIn);
            setAuthState(loggedIn ? "authenticated" : "unauthenticated");
        }
    }, [loggedIn]);

    const login = () => {
        setIsLoggedIn(true);
        setAuthState("authenticated");
    };

    const logout = () => {
        setIsLoggedIn(false);
        setAuthState("unauthenticated");
    };

    const authLoading = authState === "loading";

    return (
        <AuthContext.Provider
            value={{
                isLoggedIn,
                authState,
                authLoading,
                login,
                logout,
                setAuthState
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useLoginContext must be used within a LoginProvider");
    }
    return context;
};
